import { StaffRoutes } from "appRoutePaths";
import { IAuthContext, IAuthToken } from "context/auth";
import Cookies from "js-cookie";
import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";

const CookieKeyAuthType = "Type";
const CookieKeyAccessToken = "Token";
const CookieKeyRefreshToken = "RT";
const CookieKeyPreImpersonationToken = "PreImpersonationToken";
const CookieKeyInApp = "InApp";

export const useAuthCookiePersistence = (): IAuthContext => {
    const [authToken, setAuthToken] = useState(
        Cookies.get(CookieKeyAccessToken)
    );
    const [tokenType, setTokenType] = useState(Cookies.get(CookieKeyAuthType));
    const [refreshToken, setRefreshToken] = useState(Cookies.get(CookieKeyRefreshToken));
    const navigate = useNavigate();

    const setToken = (token: IAuthToken) => {
        const { tokenType, accessToken, refreshToken } = token || {};

        if (token === undefined) {
            Cookies.remove(CookieKeyAccessToken);
            Cookies.remove(CookieKeyAuthType);
            Cookies.remove(CookieKeyPreImpersonationToken);
            Cookies.remove(CookieKeyRefreshToken);
        } else {
            let cookieAttribs = undefined;
            if (token.expiresIn) {
                const date = new Date();
                date.setSeconds(date.getSeconds() + token.expiresIn);
                cookieAttribs = { expires: date };
            }

            Cookies.set(CookieKeyAccessToken, accessToken, cookieAttribs);
            Cookies.set(CookieKeyAuthType, tokenType, cookieAttribs);
            if (refreshToken) {
                Cookies.set(CookieKeyRefreshToken, refreshToken, cookieAttribs);
            }
        }

        setAuthToken(accessToken);
        setTokenType(tokenType);
        setRefreshToken(refreshToken);
    };

    const setPreImpersonationToken = (impersonatedToken?: string) => {
        if (impersonatedToken) {
            Cookies.set(CookieKeyPreImpersonationToken, impersonatedToken);
        } else {
            Cookies.remove(CookieKeyPreImpersonationToken);
        }
    };

    const startImpersonating = (impersonatedToken: IAuthToken) => {
        setPreImpersonationToken(authToken);
        setToken(impersonatedToken);
    };

    const stopImpersonating = () => {
        const token: IAuthToken = {
            accessToken: Cookies.get(CookieKeyPreImpersonationToken)!,
            tokenType: Cookies.get(CookieKeyAuthType)!,
            refreshToken: Cookies.get(CookieKeyRefreshToken)
        };
        setToken(token);
        setPreImpersonationToken(undefined);
        navigate(StaffRoutes.dashboard.url);
    };

    const isImpersonating = (): boolean => {
        return !!Cookies.get(CookieKeyPreImpersonationToken);
    };

    const isInWebView = !!Cookies.get(CookieKeyInApp);

    const authTokenFactory = useCallback(() => {
        return Cookies.get(CookieKeyAccessToken) ?? ""
    }, []);

    return {
        authToken,
        authTokenFactory,
        tokenType,
        isInWebView,
        setToken,
        startImpersonating,
        stopImpersonating,
        isImpersonating,
        refreshToken
    };
};
